import { PortableTextTypeComponent } from '@portabletext/react'
import React from 'react'
import { getBlockId } from '../../../utilities/blockId'
import { HorizontalLine } from '../../blocks/HorizontalLine'
import { AnnualReviewsSerializer } from '../serializers/componentTypes/AnnualReviewsSerializer'
import { BannerSerializer } from '../serializers/componentTypes/BannerSerializer'
import { BusinessLinesSerializer } from '../serializers/componentTypes/BusinessLinesSerializer'
import { CareersSerializer } from '../serializers/componentTypes/CareersSerializer'
import { CategoryTableSerializer } from '../serializers/componentTypes/CategoryTableSerializer'
import { ContactFormSerializer } from '../serializers/componentTypes/ContactFormSerializer'
import { DistributorListSerializer } from '../serializers/componentTypes/DistributorListSerializer'
import { FeaturedVideoTeaserSerializer } from '../serializers/componentTypes/FeaturedVideoTeaserSerializer'
import { FootnotesSerializer } from '../serializers/componentTypes/FootnotesSerializer'
import { FullWidthTeaserSerializer } from '../serializers/componentTypes/FullWidthTeaserSerializer'
import { FullWidthTextSerializer } from '../serializers/componentTypes/FullWidthTextBlockSerializer'
import { HeadingSerializer } from '../serializers/componentTypes/HeadingSerializer'
import { ImageGridSerializer } from '../serializers/componentTypes/ImageGridSerializer'
import { ImageTextSerializer } from '../serializers/componentTypes/ImageTextSerializer'
import { InvestmentRangeSerializer } from '../serializers/componentTypes/InvestmentRangeSerializer'
import { InvestmentStrategiesSerializer } from '../serializers/componentTypes/InvestmentStrategiesSerializer'
import { KeyDocumentsSerializer } from '../serializers/componentTypes/KeyDocumentsSerializer'
import { KeyTermsBlockSerializer } from '../serializers/componentTypes/KeyTermsBlockSerializer'
import { KpiBlockSerializer } from '../serializers/componentTypes/KpiBlockSerializer'
import { LargeTeaserSerializer } from '../serializers/componentTypes/LargeTeaserSerializer'
import { NewsletterFormSerializer } from '../serializers/componentTypes/NewsletterFormSerializer'
import { PageIntroSerializer } from '../serializers/componentTypes/PageIntroSerializer'
import { PersonContactsSerializer } from '../serializers/componentTypes/PersonContactsSerializer'
import { SharesTableSerializer } from '../serializers/componentTypes/SharesTableSerializer'
import { TwoColumnBannerSerializer } from '../serializers/componentTypes/TwoColumnBannerSerializer'
import { TwoColumnsLeftStickySerializer } from '../serializers/componentTypes/TwoColumnsLeftStickySerializer'
import { TwoColumnTextSerializer } from '../serializers/componentTypes/TwoColumnTextSerializer'
import { TwoTextsSerializer } from '../serializers/componentTypes/TwoTextsSerializer'
import { VideoAutoplayPreviewSerializer } from '../serializers/componentTypes/VideoAutoplayPreviewSerializer'
import { VideoSerializer } from '../serializers/componentTypes/VideoSerializer'
import { VideoTeaserCardSerializer } from '../serializers/componentTypes/VideoTeaserCardSerializer'
import { GetPortableTextComponentTypesOptions } from './types'

export const getLandingPageComponentTypes = ({
  additionalBlockProps,
  blockIdMap,
}: GetPortableTextComponentTypesOptions): Record<string, PortableTextTypeComponent> => ({
  annualReviews: ({ value }) => (
    <AnnualReviewsSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  banner: ({ value }) => (
    <BannerSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  bannerBlock: ({ value }) => (
    <TwoColumnBannerSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  businessLines: ({ value }) => (
    <BusinessLinesSerializer
      {...value}
      {...additionalBlockProps?.businessLines}
      id={getBlockId(value, blockIdMap)}
    />
  ),
  careers: ({ value }) => (
    <CareersSerializer
      {...value}
      {...additionalBlockProps?.careers}
      id={getBlockId(value, blockIdMap)}
    />
  ),
  categoryTableBlock: ({ value }) => (
    <CategoryTableSerializer
      {...value}
      {...additionalBlockProps?.categoryTableBlock}
      id={getBlockId(value, blockIdMap)}
    />
  ),
  contactForm: ({ value }) => (
    <ContactFormSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  distributorList: ({ value }) => (
    <DistributorListSerializer id={getBlockId(value, blockIdMap)} {...value} />
  ),
  featuredVideoTeaser: ({ value }) => (
    <FeaturedVideoTeaserSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  footnotes: ({ value }) => (
    <FootnotesSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  fullWidthTeaser: ({ value }) => (
    <FullWidthTeaserSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  fullWidthTextBlock: ({ value }) => (
    <FullWidthTextSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  heading: ({ value }) => (
    <HeadingSerializer
      {...value}
      {...additionalBlockProps?.heading}
      id={getBlockId(value, blockIdMap)}
    />
  ),
  horizontalLine: () => <HorizontalLine />,
  imageGridBlock: ({ value }) => (
    <ImageGridSerializer
      {...value}
      {...additionalBlockProps?.imageGridBlock}
      id={getBlockId(value, blockIdMap)}
    />
  ),
  imageText: ({ value }) => (
    <ImageTextSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  investmentRangeBlock: ({ value }) => (
    <InvestmentRangeSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  investmentStrategies: ({ value }) => (
    <InvestmentStrategiesSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  keyDocumentsBlock: ({ value }) => (
    <KeyDocumentsSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  keyTermsBlock: ({ value }) => (
    <KeyTermsBlockSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  kpiBlock: ({ value }) => (
    <KpiBlockSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  largeTeaser: ({ value }) => (
    <LargeTeaserSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  newsletterForm: ({ value }) => <NewsletterFormSerializer {...value} />,
  pageIntro: ({ value }) => (
    <PageIntroSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  personContacts: ({ value }) => (
    <PersonContactsSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  sharesTable: ({ value }) => <SharesTableSerializer {...value} />,
  twoColumnsLeftSticky: ({ value }) => (
    <TwoColumnsLeftStickySerializer
      {...value}
      {...additionalBlockProps?.twoColumnsLeftSticky}
      id={getBlockId(value, blockIdMap)}
    />
  ),
  twoColumnText: ({ value }) => (
    <TwoColumnTextSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  twoTexts: ({ value }) => (
    <TwoTextsSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  video: ({ value }) => (
    <VideoSerializer
      {...value}
      {...additionalBlockProps?.video}
      id={getBlockId(value, blockIdMap)}
    />
  ),
  videoAutoplayPreview: ({ value }) => (
    <VideoAutoplayPreviewSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
  videoTeaserCard: ({ value }) => (
    <VideoTeaserCardSerializer {...value} id={getBlockId(value, blockIdMap)} />
  ),
})
